import {
  type DateRangeDetails,
  type DatesDetails,
  DatesDetailsType,
  DatesType,
  type DaysDetails,
  type EmailMatch,
  type EmailSpec,
  type FilterCondition,
  type HoursDetails,
  type SmsMatch,
  type SmsSpec,
  SpecType,
  SubscriptionMatch,
  type SubscriptionSpec,
} from '../../../../types/FilterCondition'
import {
  UiActivityDateType,
  type UiActivityDateValue,
  type UiActivityMessageLinkSpec,
  type UiActivityMessageSpec,
  type UiActivitySubsSpec,
  UiChannelType,
  UiConditionType,
  type UiFilterCondition,
  type UiFilterDetail,
  UiSubscriptionConditionType,
} from '../../filterConditionTypes'
import {
  isUiActivityDateRangeValue,
  isUiActivityMessageLinkSpec,
  isUiActivityMessageSpec,
  isUiActivitySubsSpec,
} from '../activitySpecValidation'
import {
  fromLocalDate,
  toLocalDate,
  STORE_KEY_LIST,
  storeGet,
} from 'mailup-react-helpers'

export const toSubscriptionSpec = (
  uiCond: UiFilterCondition,
): Partial<FilterCondition> => {
  const cond: Partial<FilterCondition> = {
    SpecType: SpecType.SubscriptionSpec,
  }
  if (uiCond.Spec === undefined || !isUiActivitySubsSpec(uiCond.Spec, uiCond)) {
    throw new Error('Incorrect UiActivitySubsSpec')
  }
  const match = toSubscriptionMatch(uiCond.ConditionType)
  const spec: SubscriptionSpec = {
    SubscriptionMatchOperator: match,
    Dates: toDatesDetails(uiCond),
  }
  cond.Spec = spec
  return cond
}

const toSubscriptionMatch = (
  uiConditionType: UiConditionType | undefined,
): SubscriptionMatch => {
  switch (uiConditionType) {
    case UiConditionType.EmailSubscribed:
      return SubscriptionMatch.EmailSubscribed
    case UiConditionType.EmailUnsubscribed:
      return SubscriptionMatch.EmailUnsubscribed
    default:
      throw new Error(
        `SubscriptionMatch for condition type ${uiConditionType} not handled`,
      )
  }
}

const toDatesDetails = (uiCond: UiFilterCondition): DatesDetails => {
  const spec = uiCond.Spec
  const errorMessage = 'Invalid Dates for UiActivitySpec'
  if (spec === undefined || !isUiActivitySubsSpec(spec, uiCond)) {
    throw new Error(errorMessage)
  }
  switch (spec.DateType) {
    case UiActivityDateType.AnyMoment:
      if (spec.DateValue !== null) {
        throw new Error(errorMessage)
      }
      return {
        Type: DatesType.DateRangeDetails,
        Details: { From: undefined, To: undefined },
      }
    case UiActivityDateType.ExactDaysAgo:
      return mapIntegerDatesDetails(
        spec.DateValue,
        DatesType.DaysDetails,
        DatesDetailsType.Exactly,
      )
    case UiActivityDateType.LastDays:
      return mapIntegerDatesDetails(
        spec.DateValue,
        DatesType.DaysDetails,
        DatesDetailsType.Last,
      )
    case UiActivityDateType.ExactHoursAgo:
      return mapIntegerDatesDetails(
        spec.DateValue,
        DatesType.HoursDetails,
        DatesDetailsType.Exactly,
      )
    case UiActivityDateType.LastHours:
      return mapIntegerDatesDetails(
        spec.DateValue,
        DatesType.HoursDetails,
        DatesDetailsType.Last,
      )
    case UiActivityDateType.DateInterval:
      if (!isUiActivityDateRangeValue(spec.DateValue)) {
        throw new Error(errorMessage)
      }
      return {
        Type: DatesType.DateRangeDetails,
        Details: {
          From: requestDateMap(spec.DateValue.From, false),
          To: requestDateMap(spec.DateValue.To, true),
        },
      }
  }
}

const mapIntegerDatesDetails = (
  value: UiActivityDateValue,
  datesType: DatesType,
  datesDetailsType: DatesDetailsType,
): DatesDetails => {
  if (typeof value !== 'number') {
    throw new Error('Invalid Integer Date for UiActivitySpec')
  }
  return {
    Type: datesType,
    Details: {
      Value: value,
      Type: datesDetailsType,
    },
  }
}

export const toSmsSpec = (
  uiCond: UiFilterCondition,
  f: UiFilterDetail,
): Partial<FilterCondition> => {
  const cond: Partial<FilterCondition> = {
    SpecType: SpecType.SmsSpec,
  }
  const uiSpec = uiCond.Spec
  if (uiSpec === undefined || !isUiActivityMessageSpec(uiSpec, uiCond, f)) {
    throw new Error('Incorrect UiActivityMessageSpec')
  }
  const match = uiCond.ConditionType as SmsMatch
  const spec: SmsSpec = {
    SmsMatchOperator: match,
    Dates: toDatesDetails(uiCond),
    Messages: [uiSpec.Message],
    Tags: uiSpec.Tags,
  }
  cond.Spec = spec
  return cond
}

export const toEmailSpec = (
  uiCond: UiFilterCondition,
  f: UiFilterDetail,
): Partial<FilterCondition> => {
  const cond: Partial<FilterCondition> = {
    SpecType: SpecType.EmailSpec,
  }
  const uiSpec = uiCond.Spec
  if (uiSpec === undefined || !isUiActivityMessageSpec(uiSpec, uiCond, f)) {
    throw new Error('Incorrect UiActivityMessageSpec')
  }
  const match = uiCond.ConditionType as EmailMatch
  const links =
    isUiActivityMessageLinkSpec(uiSpec, uiCond, f) &&
    uiSpec.Link !== undefined &&
    uiSpec.Link.trim() !== ''
      ? [uiSpec.Link]
      : []
  const spec: EmailSpec = {
    EmailMatchOperator: match,
    Dates: toDatesDetails(uiCond),
    Messages: [uiSpec.Message],
    Tags: uiSpec.Tags,
    Links: links,
  }
  cond.Spec = spec
  return cond
}

export const getUiSubscriptionCondTypeBasedOnSpec = (
  condition: FilterCondition,
): UiConditionType | undefined => {
  const spec = condition.Spec as SubscriptionSpec
  switch (spec.SubscriptionMatchOperator) {
    case SubscriptionMatch.EmailSubscribed:
      return UiSubscriptionConditionType.EmailSubscribed
    case SubscriptionMatch.EmailUnsubscribed:
      return UiSubscriptionConditionType.EmailUnsubscribed
    default:
      return undefined
  }
}
export const getUiEmailCondTypeBasedOnSpec = (
  condition: FilterCondition,
): UiConditionType | undefined => {
  const spec = condition.Spec as EmailSpec
  return spec.EmailMatchOperator
}
export const getUiSmsCondTypeBasedOnSpec = (
  condition: FilterCondition,
): UiConditionType | undefined => {
  const spec = condition.Spec as SmsSpec
  return spec.SmsMatchOperator
}

export const fromSubscriptionSpec = (
  spec: SubscriptionSpec,
): UiActivitySubsSpec => {
  return fromDatesDetails(spec.Dates)
}

const fromDatesDetails = (dates: DatesDetails): UiActivitySubsSpec => {
  if (dates.Type === DatesType.DateRangeDetails) {
    const details = dates.Details as DateRangeDetails
    if (details.From === undefined && details.To === undefined) {
      return {
        DateType: UiActivityDateType.AnyMoment,
        DateValue: null,
      }
    }
    return {
      DateType: UiActivityDateType.DateInterval,
      DateValue: {
        From: responseDateMap(details.From, false),
        To: responseDateMap(details.To, true),
      },
    }
  }
  if (dates.Type === DatesType.DaysDetails) {
    const details = dates.Details as DaysDetails
    return {
      DateType:
        details.Type === DatesDetailsType.Exactly
          ? UiActivityDateType.ExactDaysAgo
          : UiActivityDateType.LastDays,
      DateValue: details.Value,
    }
  }
  if (dates.Type === DatesType.HoursDetails) {
    const details = dates.Details as HoursDetails
    return {
      DateType:
        details.Type === DatesDetailsType.Exactly
          ? UiActivityDateType.ExactHoursAgo
          : UiActivityDateType.LastHours,
      DateValue: details.Value,
    }
  }
  throw new Error('Invalid DatesDetails')
}

export const fromSmsSpec = (spec: SmsSpec): UiActivityMessageSpec => {
  return {
    ChannelType: UiChannelType.Sms,
    Message: spec.Messages[0],
    Tags: spec.Tags ?? [],
    ...fromDatesDetails(spec.Dates),
  }
}

export const fromEmailSpec = (
  spec: EmailSpec,
): UiActivityMessageSpec | UiActivityMessageLinkSpec => {
  return {
    ChannelType: UiChannelType.Email,
    Message: spec.Messages[0],
    Tags: spec.Tags ?? [],
    ...fromDatesDetails(spec.Dates),
    Link: spec.Links?.[0] ?? '',
  }
}

export const requestDateMap = (
  date: Date | null,
  handleTo: boolean,
): string | undefined => {
  if (!(date instanceof Date)) {
    return undefined
  }
  const timeZone = storeGet(STORE_KEY_LIST).TimeZoneCode as string
  // remove offset to handle case in which browser timezone is different from list timeZone
  const mappedDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000)
  if (handleTo) {
    // if field To, set date as 00:00 of day after
    mappedDate.setUTCHours(23, 59, 59)
    mappedDate.setSeconds(mappedDate.getSeconds() + 1)
  }
  return fromLocalDate(mappedDate, timeZone).toISOString()
}
export const responseDateMap = (
  date: string | undefined,
  handleTo: boolean,
): Date | null => {
  if (date === undefined || date === '') {
    return null
  }
  const timeZone = storeGet(STORE_KEY_LIST).TimeZoneCode as string
  // add offset to handle case in which browser timezone is different from list timeZone
  const mappedDate = new Date(
    new Date(date).getTime() + new Date(date).getTimezoneOffset() * 60000,
  )
  if (handleTo) {
    // if field To, remove one second to show the day before at 23:59 on datepickers
    mappedDate.setSeconds(mappedDate.getSeconds() - 1)
  }
  return toLocalDate(new Date(mappedDate), timeZone)
}
